import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowLeft, MessageSquare, Send, User, Instagram, Facebook, Linkedin, TrendingUp, Heart, X, Users, Share2, Twitter, Link as LinkIcon, Check } from 'lucide-react';
import { trackArticleView } from '../components/Chatbot';
import LanguagePicker, { useLanguage, useTranslation, translateText, t } from '../components/LanguagePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ author_name: '', author_email: '', content: '' });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentMsg, setCommentMsg] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeAnim, setLikeAnim] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatedContent, setTranslatedContent] = useState('');
  const [translating, setTranslating] = useState(false);
  const { lang } = useTranslation();
  const commentRef = useRef(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/blog/articles/${articleId}`);
        setArticle(res.data.article);
        setComments(res.data.comments || []);
        trackArticleView(res.data.article);
      } catch (err) {
        console.error('Failed to fetch article', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [articleId]);

  // Translate article when language changes
  useEffect(() => {
    if (!article || lang === 'id') {
      setTranslatedTitle('');
      setTranslatedContent('');
      return;
    }
    let cancelled = false;
    const doTranslate = async () => {
      setTranslating(true);
      const [title, content] = await Promise.all([
        translateText(article.title, lang, 'title'),
        translateText(article.content, lang, 'article'),
      ]);
      if (!cancelled) {
        setTranslatedTitle(title);
        setTranslatedContent(content);
        setTranslating(false);
      }
    };
    doTranslate();
    return () => { cancelled = true; };
  }, [lang, article]);

  const handleLike = async () => {
    if (liked) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/articles/${articleId}/like`);
      setArticle(prev => ({ ...prev, likes: res.data.likes }));
      setLiked(true);
      setLikeAnim(true);
      setTimeout(() => setLikeAnim(false), 600);
    } catch (err) { console.error(err); }
  };

  const handleViewersClick = async () => {
    setShowViewers(true);
    setViewersLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog/articles/${articleId}/viewers`);
      setViewers(res.data.viewers || []);
    } catch (err) { console.error(err); }
    finally { setViewersLoading(false); }
  };

  const scrollToComments = () => {
    commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleComment = async (e) => {
    e.preventDefault();
    setSubmittingComment(true);
    setCommentMsg('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/blog/articles/${articleId}/comments`, {
        article_id: articleId,
        ...commentForm
      });
      setComments(prev => [...prev, res.data.comment]);
      setCommentForm({ author_name: '', author_email: '', content: '' });
      setCommentMsg(t('comment_success'));
    } catch (err) {
      setCommentMsg(t('comment_error'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays < 7) return `${diffDays} hari lalu`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} minggu lalu`;
    return formatDate(dateStr);
  };

  const stripHtml = (html) => html ? html.replace(/<[^>]+>/g, '').slice(0, 200) : '';
  const shareUrl = `${window.location.origin}/api/share/${articleId}`;

  // Inject OG meta tags + JSON-LD structured data into <head> for SEO
  useEffect(() => {
    if (!article) return;
    const desc = stripHtml(article.excerpt || article.content);
    const articleUrl = `${window.location.origin}/blog/${articleId}`;
    const imageUrl = article.featured_image ? resolveImageUrl(article.featured_image) : '';
    document.title = `${article.title} — Pakde Heru | Strategic Thinker`;

    const tags = [
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: articleUrl },
      { property: 'og:title', content: article.title },
      { property: 'og:description', content: desc },
      { property: 'og:site_name', content: 'Pakde Heru — Strategic Thinker' },
      { property: 'og:locale', content: 'id_ID' },
      { property: 'article:published_time', content: article.published_at || article.created_at || '' },
      { property: 'article:author', content: 'Pakde Heru' },
      { property: 'article:section', content: article.category || 'Behavioral Finance' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: article.title },
      { name: 'twitter:description', content: desc },
      { name: 'description', content: desc },
      { name: 'author', content: 'Pakde Heru' },
      { name: 'keywords', content: (article.tags || []).join(', ') + ', behavioral finance, wealth logic, pakde heru' },
    ];
    if (imageUrl) {
      tags.push({ property: 'og:image', content: imageUrl });
      tags.push({ property: 'og:image:width', content: '1200' });
      tags.push({ property: 'og:image:height', content: '630' });
      tags.push({ name: 'twitter:image', content: imageUrl });
    }
    // article:tag for each tag
    (article.tags || []).forEach(tag => {
      tags.push({ property: 'article:tag', content: tag });
    });

    const created = [];
    tags.forEach(({ property, name, content }) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (property) el.setAttribute('property', property);
        if (name) el.setAttribute('name', name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute('href', articleUrl);

    // JSON-LD structured data (Article schema)
    let jsonLd = document.getElementById('article-jsonld');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'article-jsonld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
      created.push(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: desc,
      image: imageUrl || undefined,
      author: { '@type': 'Person', name: 'Pakde Heru', url: 'https://facebook.com/pakdeheruu' },
      publisher: { '@type': 'Organization', name: 'Pakde Heru — Strategic Thinker', url: window.location.origin },
      datePublished: article.published_at || article.created_at,
      dateModified: article.updated_at || article.published_at || article.created_at,
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      keywords: (article.tags || []).join(', '),
      articleSection: article.category || 'Behavioral Finance',
      inLanguage: 'id-ID',
      interactionStatistic: [
        { '@type': 'InteractionCounter', interactionType: 'https://schema.org/LikeAction', userInteractionCount: article.likes || 0 },
        { '@type': 'InteractionCounter', interactionType: 'https://schema.org/CommentAction', userInteractionCount: comments.length },
      ],
    });

    return () => { created.forEach(el => el.remove()); };
  }, [article, articleId]);

  if (loading) return <div className="blog-page"><div className="article-loading">{t('loading_articles')}</div></div>;
  if (!article) return (
    <div className="blog-page">
      <div className="article-not-found">
        <h2>{t('article_not_found')}</h2>
        <Link to="/" className="blog-back-link"><ArrowLeft size={18} /> {t('back_to_blog')}</Link>
      </div>
    </div>
  );

  return (
    <div className="blog-page" data-testid="article-page">
      {/* Header */}
      <header className="blog-header">
        <div className="blog-header-inner">
          <Link to="/" className="blog-logo">
            <TrendingUp size={28} />
            <div>
              <span className="blog-logo-title">Pakde Heru</span>
              <span className="blog-logo-sub">{t('hero_subtitle')}</span>
            </div>
          </Link>
          <nav className="blog-nav">
            <Link to="/" className="blog-nav-link">{t('nav_blog')}</Link>
            <Link to="/buku" className="blog-nav-link">{t('nav_buku')}</Link>
            <a href="https://wa.me/628118007799?text=Halo%20Pakde%20Heru%2C%20saya%20ingin%20bergabung%20di%20komunitas!" target="_blank" rel="noopener noreferrer" className="blog-nav-link komunitas-link" data-testid="nav-komunitas"><Users size={16} /> {t('nav_komunitas')}</a>
            <div className="blog-social-links">
              <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer"><Facebook size={18} /></a>
              <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer"><Linkedin size={18} /></a>
            </div>
          </nav>
          <LanguagePicker />
        </div>
      </header>

      {/* Article */}
      <article className="article-container" data-testid="article-content">
        <div className="article-inner">
          <Link to="/" className="blog-back-link"><ArrowLeft size={18} /> {t('back_to_blog')}</Link>

          {/* Featured Image */}
          {article.featured_image && (
            <div className="article-featured-img" data-testid="article-featured-img">
              <img src={resolveImageUrl(article.featured_image)} alt={article.title} />
            </div>
          )}

          <div className="article-header">
            {article.category && <span className="article-category">{article.category}</span>}
            <h1 className="article-title" data-testid="article-title">
              {translatedTitle || article.title}
              {translating && <span className="translating-indicator" data-testid="translating-indicator">{t('translating')}</span>}
            </h1>
            <div className="article-meta">
              <span className="article-author"><User size={16} /> {article.author}</span>
              <span><Calendar size={16} /> {formatDate(article.published_at)}</span>
              <button className="meta-btn" onClick={handleViewersClick} data-testid="views-btn">
                <Eye size={16} /> {article.views} {t('views')}
              </button>
              <button className={`meta-btn meta-like-btn ${liked ? 'liked' : ''} ${likeAnim ? 'like-pop' : ''}`} onClick={handleLike} data-testid="meta-like-btn">
                <Heart size={16} fill={liked ? '#d9fb06' : 'none'} /> {article.likes || 0} {t('likes')}
              </button>
              <button className="meta-btn" onClick={scrollToComments} data-testid="meta-comment-btn">
                <MessageSquare size={16} /> {comments.length} {t('comments_label')}
              </button>
            </div>
            {article.tags && article.tags.length > 0 && (
              <div className="article-tags">
                {article.tags.map((tag, i) => (
                  <Link to={`/tag/${encodeURIComponent(tag)}`} key={i} className="blog-tag blog-tag-link" data-testid={`tag-${tag}`}>
                    <Tag size={12} /> {tag}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="article-body" dangerouslySetInnerHTML={{ __html: translatedContent || article.content }} />

          {/* Soft CTA — Follow Facebook */}
          <div data-testid="article-cta" style={{
            margin: '2.5rem 0 1.5rem', padding: '1.5rem', borderRadius: '12px',
            background: 'linear-gradient(135deg, rgba(66,103,178,0.08) 0%, rgba(217,251,6,0.04) 100%)',
            border: '1px solid rgba(66,103,178,0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <p style={{ color: '#EDEDED', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 8px' }}>
                  Suka dengan perspektif ini? Pembahasan tentang cara berpikir kritis soal uang berlanjut setiap hari di Facebook.
                </p>
                <p style={{ color: '#9CA3AF', fontSize: '0.8rem', margin: 0 }}>
                  Bergabung dengan komunitas pembaca yang menolak berpikir mainstream tentang keuangan.
                </p>
              </div>
              <a
                href="https://facebook.com/pakdeheruu"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-follow-facebook"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: '#4267B2', color: '#FFFFFF', borderRadius: '8px',
                  padding: '10px 20px', fontSize: '0.85rem', fontWeight: 600,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  transition: 'opacity 0.2s',
                }}
              >
                <Facebook size={16} /> Follow Pakde Heru
              </a>
            </div>
          </div>

          {/* Like & Share */}
          <div className="article-actions">
            <button className={`like-btn ${liked ? 'liked' : ''} ${likeAnim ? 'like-pop' : ''}`} onClick={handleLike} data-testid="like-btn">
              <Heart size={20} fill={liked ? '#d9fb06' : 'none'} /> {article.likes || 0} {t('likes')}
            </button>
          </div>

          {/* Social Share */}
          <div className="article-share" data-testid="article-share">
            <span className="share-label"><Share2 size={16} /> {t('share_article')}</span>
            <div className="share-buttons">
              <a href={`https://wa.me/?text=${encodeURIComponent(article.title + '\n\n' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="share-btn share-wa" data-testid="share-whatsapp" title="WhatsApp">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.633-1.46A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.214 0-4.26-.723-5.916-1.948l-.424-.317-2.75.867.832-2.685-.348-.463A9.716 9.716 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="share-btn share-fb" data-testid="share-facebook" title="Facebook">
                <Facebook size={16} />
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="share-btn share-tw" data-testid="share-twitter" title="X / Twitter">
                <Twitter size={16} />
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="share-btn share-li" data-testid="share-linkedin" title="LinkedIn">
                <Linkedin size={16} />
              </a>
              <button className={`share-btn share-copy ${copied ? 'copied' : ''}`} onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); }} data-testid="share-copy" title="Copy Link">
                {copied ? <Check size={16} /> : <LinkIcon size={16} />}
              </button>
            </div>
          </div>

          {/* Comments */}
          <section className="comments-section" ref={commentRef} data-testid="comments-section">
            <h3 className="comments-title"><MessageSquare size={22} /> {t('comments_title')} ({comments.length})</h3>

            {comments.map((comment) => (
              <div key={comment.id} className="comment-item" data-testid={`comment-${comment.id}`}>
                <div className="comment-header">
                  <span className="comment-author"><User size={14} /> {comment.author_name}</span>
                  <span className="comment-date" title={formatDate(comment.created_at)}>{formatRelativeDate(comment.created_at)}</span>
                </div>
                <p className="comment-content">{comment.content}</p>
                {comment.replies && comment.replies.map((reply) => (
                  <div key={reply.id} className="comment-reply">
                    <div className="comment-header">
                      <span className="comment-author reply-author"><User size={14} /> {reply.author_name}</span>
                      <span className="comment-date" title={formatDate(reply.created_at)}>{formatRelativeDate(reply.created_at)}</span>
                    </div>
                    <p className="comment-content">{reply.content}</p>
                  </div>
                ))}
              </div>
            ))}

            <form onSubmit={handleComment} className="comment-form" data-testid="comment-form">
              <h4>{t('leave_comment')}</h4>
              <div className="comment-form-row">
                <input type="text" placeholder={t('name_placeholder')} value={commentForm.author_name} onChange={e => setCommentForm(p => ({ ...p, author_name: e.target.value }))} required data-testid="comment-name" />
                <input type="email" placeholder={t('email_placeholder')} value={commentForm.author_email} onChange={e => setCommentForm(p => ({ ...p, author_email: e.target.value }))} required data-testid="comment-email" />
              </div>
              <textarea placeholder={t('comment_placeholder')} value={commentForm.content} onChange={e => setCommentForm(p => ({ ...p, content: e.target.value }))} required rows={4} data-testid="comment-content" />
              <button type="submit" disabled={submittingComment} data-testid="comment-submit">
                <Send size={16} /> {submittingComment ? t('submitting_comment') : t('submit_comment')}
              </button>
              {commentMsg && <p className="comment-msg">{commentMsg}</p>}
            </form>
          </section>
        </div>
      </article>

      {/* Viewers Modal */}
      {showViewers && (
        <div className="viewers-overlay" onClick={() => setShowViewers(false)} data-testid="viewers-modal">
          <div className="viewers-modal" onClick={e => e.stopPropagation()}>
            <div className="viewers-modal-header">
              <h3><Eye size={20} /> {t('viewed_by')} ({article.views})</h3>
              <button onClick={() => setShowViewers(false)} className="viewers-close" data-testid="viewers-close"><X size={20} /></button>
            </div>
            <div className="viewers-modal-body">
              {viewersLoading ? (
                <p style={{ color: '#A1A1AA', textAlign: 'center', padding: '2rem' }}>{t('loading')}</p>
              ) : viewers.length === 0 ? (
                <p style={{ color: '#71717A', textAlign: 'center', padding: '2rem' }}>{t('no_viewer_data')}</p>
              ) : (
                <ul className="viewers-list">
                  {viewers.map((v, i) => (
                    <li key={i} className="viewer-item" data-testid={`viewer-${i}`}>
                      <div className="viewer-avatar"><User size={16} /></div>
                      <div className="viewer-info">
                        <span className="viewer-name">{v.name}</span>
                        <span className="viewer-time">{v.view_count > 1 ? `${v.view_count}x ${t('viewed_times')}` : t('viewed_once')} {v.last_viewed ? `— ${formatDate(v.last_viewed)}` : ''}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="blog-footer">
        <div className="blog-footer-inner">
          <p>&copy; 2026 {t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePage;
